import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthSession } from '@supabase/supabase-js';
import { from, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { selectSession } from '../store/auth/auth.selectors';
import { SupabaseService } from '../services/supabase.service';
import { CREATE_PASSWORD_ROUTE, mustChangePassword } from '../auth/password-gate';
import * as AuthActions from '../store/auth/auth.actions';

export const authGuard: CanActivateFn = (route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const supabase = inject(SupabaseService);

  // Check if we are in a password recovery flow.
  // Deliberately ahead of the must-change gate below: recovery is the more
  // specific state, and completing /reset-password clears the flag anyway.
  const isRecovery = window.location.hash.includes('type=recovery');
  if (isRecovery && !state.url.includes('/reset-password')) {
    return of(router.createUrlTree(['/reset-password']));
  }

  /**
   * Accounts still on their emailed temporary password get no further than the
   * password screen. Applied on BOTH paths below — a deep link with an empty
   * store resolves through getSession(), and gating only the fast path would let
   * that walk straight into the app.
   */
  const gate = (session: AuthSession): boolean | UrlTree =>
    mustChangePassword(session) ? router.createUrlTree([CREATE_PASSWORD_ROUTE]) : true;

  return store.select(selectSession).pipe(
    take(1),
    switchMap(session => {
      // If session exists in store, allow access
      if (session) {
        return of(gate(session));
      }

      // Otherwise, check Supabase for persisted session
      return from(supabase.client.auth.getSession()).pipe(
        map(({ data }) => {
          if (data.session) {
            // Restore session to store
            store.dispatch(AuthActions.sessionLoaded({ session: data.session }));
            return gate(data.session);
          }
          // No session found, redirect to login.
          // A UrlTree rather than navigate()+false: an imperative navigation from
          // inside a guard produces a NavigationCancel, which app.ts does not
          // handle, so that failure mode would be silent.
          console.log('No session found in auth guard, redirecting to login');
          return router.createUrlTree(['/login']);
        })
      );
    })
  );
};
